/**
 * Pick an Inbox channel to post to (TRUS-686: "Post to channel" steps and
 * automation actions). Lists the org's group channels the viewer can see —
 * never DMs or archived channels. When `privateOnly` is set (a member
 * workflow, or a trigger that carries member data) only private channels are
 * offered; a saved channel that is no longer allowed is shown with a warning
 * so it can be replaced rather than silently dropped.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Lock } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { messagingApi, type Channel } from '@/services/messagingApi';

export const channelPickerKey = ['notify-channel-picker'] as const;

export function postableChannels(channels: Channel[], privateOnly: boolean): Channel[] {
  return channels
    .filter((c) => c && !c.is_dm && c.channel_type !== 'direct' && !c.is_archived && c.dm_member_id == null)
    .filter((c) => !privateOnly || c.channel_type === 'private')
    .sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name));
}

interface ChannelPickerProps {
  id: string;
  value: number | null;
  onChange: (channelId: number | null) => void;
  privateOnly: boolean;
  disabled?: boolean;
}

export function ChannelPicker({ id, value, onChange, privateOnly, disabled }: ChannelPickerProps) {
  const { t } = useAppTranslation();
  const query = useQuery({
    queryKey: channelPickerKey,
    queryFn: () => messagingApi.listChannels(),
    staleTime: 60_000,
  });
  const all = Array.isArray(query.data) ? query.data : [];
  const options = postableChannels(all, privateOnly);
  const selected = value ? all.find((c) => c.id === value) : undefined;
  // Only judged once the list is in; before that every saved id looks unknown.
  const notAllowed = query.isSuccess && !!value && !options.some((c) => c.id === value);

  return (
    <div className="space-y-1.5" data-testid="notify-channel-picker">
      <label htmlFor={id} className="block text-sm font-medium">
        {t('workflows.notify.channel', 'Channel')}
      </label>
      <select
        id={id}
        className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        value={value ? String(value) : ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        disabled={disabled || query.isLoading}
      >
        <option value="">
          {query.isLoading
            ? t('workflows.notify.loadingChannels', 'Loading channels…')
            : t('workflows.notify.pickChannel', 'Pick a channel')}
        </option>
        {notAllowed && (
          <option value={String(value)}>
            {selected
              ? `#${selected.display_name || selected.name}`
              : t('workflows.notify.unknownChannel', 'Channel {{id}}', { id: value })}
          </option>
        )}
        {options.map((c) => (
          <option key={c.id} value={String(c.id)}>
            {`#${c.display_name || c.name}${
              c.channel_type === 'private' ? ` (${t('workflows.notify.private', 'private')})` : ''
            }`}
          </option>
        ))}
      </select>
      {privateOnly && (
        <p className="flex items-start gap-1 text-xs text-muted-foreground">
          <Lock className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
          {t(
            'workflows.notify.privateOnlyHint',
            'Only private channels are listed: this is about members, and posts never include member details.',
          )}
        </p>
      )}
      {notAllowed && (
        <p className="flex items-start gap-1 text-xs text-destructive" role="alert" data-testid="notify-channel-not-allowed">
          <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
          {privateOnly && selected && selected.channel_type !== 'private'
            ? t('workflows.notify.channelNotPrivate', 'This channel is not private. Pick a private channel.')
            : t('workflows.notify.channelUnavailable', 'This channel is no longer available. Pick another one.')}
        </p>
      )}
      {!query.isLoading && options.length === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="notify-channel-none">
          {privateOnly
            ? t('workflows.notify.noPrivateChannels', 'There are no private channels you can post to yet. Create one in the Inbox first.')
            : t('workflows.notify.noChannels', 'There are no channels you can post to yet. Create one in the Inbox first.')}
        </p>
      )}
    </div>
  );
}

export default ChannelPicker;
